import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  Edit2,
  FileText,
  Loader2,
  Pause,
  Play,
  Plus,
  RotateCw,
  Send,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../../components/ui/Modal'
import RecurringFormModal from '../../components/billing/RecurringFormModal'
import RecurringMetrics from '../../components/billing/RecurringMetrics'
import ColumnSortTh from '../../components/ui/ColumnSortTh'
import { formatCents } from '../../lib/money'
import {
  RecurringInvoicePeriodicityLabel,
  type RecurringInvoiceDto,
  type RecurringInvoiceListResponse,
  type RecurringInvoiceStatusT,
} from '@jr2os/shared'

const STATUS_STYLES: Record<RecurringInvoiceStatusT, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  PAUSED: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  CANCELED: 'bg-slate-500/15 t-secondary border-slate-500/30',
  COMPLETED: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
}

/** Lo que enseña la columna «Importe/periodo»: la suma de las líneas (cantidad × precio), sin descuento ni IVA. */
const planTotal = (r: RecurringInvoiceDto) =>
  r.lines.reduce((acc, l) => acc + Math.round(Number(l.quantity) * l.unitPriceCents), 0)

/** Meses de cada periodicidad: ordenar por «Periodicidad» va de la más corta a la más larga, no alfabéticamente. */
const PERIODICITY_MONTHS: Record<string, number> = {
  MONTHLY: 1,
  BIMONTHLY: 2,
  QUARTERLY: 3,
  SEMIANNUAL: 6,
  YEARLY: 12,
}
const STATUS_ORDER = ['ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELED']

export default function RecurringSettingsPage() {
  const { t } = useTranslation('settings')
  const [items, setItems] = useState<RecurringInvoiceDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<RecurringInvoiceDto | 'new' | null>(null)

  // Orden por columna (en el navegador: la lista es corta y no se pagina), guardado en la URL. Sin orden, el de siempre.
  const [searchParams, setSearchParams] = useSearchParams()
  const sort = searchParams.get('sort') ?? ''
  const dir: 'asc' | 'desc' = searchParams.get('dir') === 'desc' ? 'desc' : 'asc'
  const onSort = (key: string) => {
    const next = new URLSearchParams(searchParams)
    if (sort !== key) {
      next.set('sort', key)
      next.set('dir', 'asc')
    } else if (dir === 'asc') {
      next.set('dir', 'desc')
    } else {
      next.delete('sort')
      next.delete('dir')
    }
    setSearchParams(next, { replace: true })
  }
  const th = (key: string, label: string, align: 'left' | 'right' | 'center') => (
    <ColumnSortTh
      sortKey={key}
      label={label}
      align={align}
      active={sort === key}
      dir={dir}
      onSort={onSort}
      ariaLabel={t('recurring.table.sortBy', { col: label })}
    />
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<RecurringInvoiceListResponse>('/recurring-invoices')
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // El dashboard de ARR/MRR se recalcula cuando la tabla cambia (pausar, editar, eliminar…), no por la carga inicial: él ya
  // pide la suya al montarse, y recargar al llegar la lista duplicaría la petición.
  const [metricsVersion, setMetricsVersion] = useState(0)
  const reload = useCallback(async () => {
    await load()
    setMetricsVersion((v) => v + 1)
  }, [load])

  const sortedItems = useMemo(() => {
    if (!sort) return items
    const keyOf = (r: RecurringInvoiceDto): string | number => {
      switch (sort) {
        case 'name':
          return r.name.toLowerCase()
        case 'client':
          return r.clientDisplayName.toLowerCase()
        case 'periodicity':
          return PERIODICITY_MONTHS[r.periodicity] ?? 0
        case 'first':
          return new Date(r.firstRunDate).getTime()
        case 'next':
          return new Date(r.nextRunDate).getTime()
        case 'total':
          return planTotal(r)
        case 'generated':
          return r.generatedCount
        case 'status':
          return STATUS_ORDER.indexOf(r.status)
        default:
          return 0
      }
    }
    const sign = dir === 'desc' ? -1 : 1
    // `sort` de JS es estable: lo que empata conserva el orden de siempre.
    return [...items].sort((x, y) => {
      const a = keyOf(x)
      const b = keyOf(y)
      return typeof a === 'string' && typeof b === 'string'
        ? sign * a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })
        : sign * (Number(a) - Number(b))
    })
  }, [items, sort, dir])

  const action = async (id: string, op: 'pause' | 'resume' | 'cancel' | 'generate-now') => {
    try {
      const r = await api.post<{ invoiceId?: string }>(`/recurring-invoices/${id}/${op}`, {})
      if (op === 'generate-now' && r?.invoiceId) {
        if (await confirmDialog(t('recurring.draftCreatedConfirm'))) {
          window.location.href = `/billing/${r.invoiceId}`
          return
        }
      }
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  const onDelete = async (r: RecurringInvoiceDto) => {
    if (
      !(await confirmDialog({
        message: t('recurring.deleteConfirm', { name: r.name }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/recurring-invoices/${r.id}`)
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('recurring.title')}</h2>
          <p className="t-muted text-sm mt-1">
            {t('recurring.subtitlePre')}
            <strong>{t('recurring.subtitleDraft')}</strong>
            {t('recurring.subtitlePost')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
        >
          <Plus size={14} /> {t('recurring.newButton')}
        </button>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <RecurringMetrics refreshKey={metricsVersion} />

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              {th('name', t('recurring.table.name'), 'left')}
              {th('client', t('recurring.table.client'), 'left')}
              {th('periodicity', t('recurring.table.periodicity'), 'left')}
              {th('first', t('recurring.table.first'), 'left')}
              {th('next', t('recurring.table.next'), 'left')}
              {th('total', t('recurring.table.totalPerPeriod'), 'right')}
              {th('generated', t('recurring.table.generated'), 'center')}
              {th('status', t('recurring.table.status'), 'left')}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('recurring.loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center">
                  <RotateCw size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {t('recurring.empty')}
                  </p>
                </td>
              </tr>
            )}
            {sortedItems.map((r) => {
              const total = planTotal(r)
              return (
                <tr key={r.id} className="border-t border-base/40 hover:bg-hover">
                  <td className="px-3 py-2 t-primary font-medium">{r.name}</td>
                  <td className="px-3 py-2 t-secondary">
                    <Link to="/clients" className="hover:underline">
                      {r.clientDisplayName}
                    </Link>
                    {r.opportunityCode && (
                      <span className="text-[10px] t-faint font-mono ml-1">
                        ({r.opportunityCode})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs t-secondary">
                    {t('enums:RecurringInvoicePeriodicity.' + r.periodicity)} {t('recurring.dayParen', { day: r.dayOfMonth })}
                  </td>
                  {/* La primera cuota: la fecha en que se generó; si aún no ha generado ninguna, la prevista (en tenue). */}
                  <td
                    className={`px-3 py-2 text-xs font-mono ${
                      r.generatedCount > 0 ? 't-secondary' : 't-faint italic'
                    }`}
                    title={r.generatedCount > 0 ? undefined : t('recurring.firstPlanned')}
                    data-testid="recurring-first"
                  >
                    {new Date(r.firstRunDate).toLocaleDateString(currentLocaleTag(), {
                      timeZone: 'UTC',
                    })}
                  </td>
                  <td className="px-3 py-2 text-xs t-secondary font-mono">
                    {new Date(r.nextRunDate).toLocaleDateString(currentLocaleTag(), {
                      timeZone: 'UTC',
                    })}
                  </td>
                  <td className="px-3 py-2 text-right t-primary font-mono">
                    {formatCents(total)}
                  </td>
                  <td className="px-3 py-2 text-center text-xs t-muted">{r.generatedCount}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STATUS_STYLES[r.status]}`}>
                      {t('enums:RecurringInvoiceStatus.' + r.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void action(r.id, 'generate-now')}
                        disabled={r.status !== 'ACTIVE'}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-emerald-700 dark:hover:text-emerald-300 disabled:opacity-30"
                        title={t('recurring.actions.generateNow')}
                      >
                        <Send size={12} />
                      </button>
                      {r.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          onClick={() => void action(r.id, 'pause')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-amber-500/10 t-muted hover:text-amber-700 dark:hover:text-amber-300"
                          title={t('recurring.actions.pause')}
                        >
                          <Pause size={12} />
                        </button>
                      ) : r.status === 'PAUSED' ? (
                        <button
                          type="button"
                          onClick={() => void action(r.id, 'resume')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-emerald-500/10 t-muted hover:text-emerald-700 dark:hover:text-emerald-300"
                          title={t('recurring.actions.resume')}
                        >
                          <Play size={12} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                        title={t('recurring.actions.edit')}
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(r)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                        title={t('recurring.actions.delete')}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <RecurringFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await reload()
          }}
        />
      )}
    </div>
  )
}


void FileText // suprime unused warning
